import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MatDialog } from '@angular/material/dialog';
import { merge, Observable, of } from 'rxjs';
import { map, switchMap, catchError, filter, tap, take } from 'rxjs/operators';
import { ManagementApiService } from '../management-api.service';
import { AddLockItemDialogComponent } from '@emm/shared/dialogs/add-emergency-item-dialog/add-lock-item-dialog.component';
import * as RA from 'ramda-adjunct';
import {OperatorLocksActions} from '@emm/management/store/emergency-items/operator-locks.actions';
import {Item} from '@emm/core/models/item.model';
import {NotificationService} from '@emm/shared/services/notification.service';
import {ItemGroupingsActions} from '@emm/management/store/item-groupings/item-groupings.actions';
import {Node} from '@emm/core/models/node.model';

@Injectable({ providedIn: 'root' })
export class OperatorLocksEffects {

    loadEmergencyLocationStatuses$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OperatorLocksActions.loadAllLocationStatuses),
            switchMap(() =>
                this.api.getEmergencyStatuses().pipe(
                    map((statuses) =>
                        OperatorLocksActions.loadAllLocationStatusesSuccess({ statuses })
                    ),
                    catchError(({ error }) =>
                        of(OperatorLocksActions.loadAllLocationStatusesFailure({ error }))
                    )
                )
            )
        )
    );

    loadItemsByEmergency$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OperatorLocksActions.loadItemsByLock),
            switchMap(({ tag }) =>
                this.api.getItemsByEmergency(tag).pipe(
                    map((emergencyMappings) =>
                        OperatorLocksActions.loadItemsByLockSuccess({ emergencyMappings })
                    ),
                    catchError(({ error }) =>
                        of(OperatorLocksActions.loadItemsByLockFailure({ error }))
                    )
                )
            )
        )
    );

    openAddDialog$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OperatorLocksActions.openAddDialog),
            switchMap(() => {
                const dialogRef = this.dialog.open(AddLockItemDialogComponent, {
                    width: '600px',
                    data: {},
                });

                return dialogRef.afterClosed().pipe(
                    filter((result) => result && result === 'submit'),
                    tap((_) => this.notificationService.success('Added Location Status Successfully')),
                    map(() => OperatorLocksActions.loadAllLocationStatuses())
                );
            })
        )
    );

    uploadChildItems$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OperatorLocksActions.addChildItem),
            switchMap(({ emergencyTag, file }) =>
                this.api.uploadOperatorLockItems(emergencyTag, file).pipe(
                    map((_) => OperatorLocksActions.addChildItemSuccess()),
                    catchError(({ error }) => of(OperatorLocksActions.addChildItemFailure({ error })))
                )
            )
        )
    );

    removeChildItem$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OperatorLocksActions.removeChildItem),
            switchMap(({ emergencyTag, itemTag }) =>
                this.api.deleteEmergencyItem(emergencyTag, itemTag).pipe(
                    map((_) => OperatorLocksActions.removeChildItemSuccess()),
                    catchError(({ error }) => of(OperatorLocksActions.removeChildItemFailure({ error })))
                )
            )
        )
    );

    updateStatusMessage$ = createEffect(() =>
        this.actions$.pipe(
            ofType(OperatorLocksActions.updateStatusMessage),
            switchMap(({ emergencyTag, statusMessage, active, subType }) =>
                this.api.updateStatus(emergencyTag, statusMessage, active, subType).pipe(
                    map((_) => OperatorLocksActions.updateStatusMessageSuccess()),
                    catchError(({ error }) => of(OperatorLocksActions.updateStatusMessageFailure({ error })))
                )
            )
        )
    );

    static mapItemGroupingsToNode(item: Item): Node<Item> {
        return {
            data: item,
            hasChildren: item.grouping,
            id: item.tag,
            name: item.name
        };
    }

    constructor(
        private actions$: Actions,
        private api: ManagementApiService,
        public dialog: MatDialog,
        private notificationService: NotificationService
    ) {}

    getAllChildItems(): Observable<Node<Item>[]> {
        const success$ = this.actions$.pipe(
            ofType(ItemGroupingsActions.loadAllChildItemsSuccess),
            map((payload) => payload.items),
            map((items: Item[]) =>
                RA.isNonEmptyArray(items) ? items.map(OperatorLocksEffects.mapItemGroupingsToNode) : []
            )
        );

        const failure$ = this.actions$.pipe(
            ofType(ItemGroupingsActions.loadAllChildItemsFailure),
            map(() => [])
        );

        return merge(success$, failure$).pipe(take(1));
    }

}
